import React from 'react';
import { css } from '@emotion/css';
import dayjs from 'dayjs';

import { Grid, TextField, MenuItem, useTheme, Theme, SxProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

interface AppraisalDateRangeProps {
  range: string;
  start: string;
  end: string;
  onRangeChange: (value: string) => void;
  onDateChange: (event: any, name: string) => void;
  disablePickers: boolean;
  /** Rendered last in the row, level with the inputs (the tab's Clear Filters). */
  trailing?: React.ReactNode;
}

//////////////////////////////////////// COMPONENT ////////////////////////////////////////
const AppraisalDateRange: React.FC<AppraisalDateRangeProps> = (props: AppraisalDateRangeProps) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const sxStyles = useSxStyles();

  const { range, start, end, onRangeChange, onDateChange, disablePickers, trailing } = props;

  const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRangeChange(event.target.value);
  };

  const handleDateChange = (name: string) => (value: any) => {
    onDateChange(value, name);
  };

  return (
    <>
      <div className={styles.main}>
        <Grid container spacing={2} alignItems='center'>
          <Grid size={{ xs: 12, md: 'grow' }}>
            <TextField
              select
              fullWidth
              label='Quarterly Range'
              variant='outlined'
              margin='none'
              value={range}
              onChange={handleRangeChange}
            >
              <MenuItem value={`this`}>This Quarter (Last 3 Months)</MenuItem>
              <MenuItem value={`last`}>Last Quarter (3-6 Months Ago)</MenuItem>
              <MenuItem value={`custom`}>Custom (Max 3-Month Quarter)</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <DatePicker
              disabled={disablePickers}
              orientation='portrait'
              format='MM/DD/YYYY'
              label='Start Date (From)'
              value={dayjs(start)}
              onChange={handleDateChange('start')}
              sx={sxStyles.date}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  size: 'small',
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <DatePicker
              disabled={disablePickers}
              orientation='portrait'
              format='MM/DD/YYYY'
              label='End Date (To)'
              value={dayjs(end)}
              onChange={handleDateChange('end')}
              sx={sxStyles.date}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  size: 'small',
                },
              }}
            />
          </Grid>
          {trailing ? <Grid size='auto'>{trailing}</Grid> : null}
        </Grid>
      </div>
    </>
  );
};

export default AppraisalDateRange;

//////////////////////////////////////// STYLES ////////////////////////////////////////
const useStyles = (theme: Theme) => {
  return {
    main: css`
      margin-bottom: ${theme.spacing(2)};
    `,
  };
};

const useSxStyles = (): Record<string, SxProps<Theme> | undefined> => ({
  date: {
    width: '100%',
  },
});
